/**
 * useBusinessHours — Unit 6 of the listing-conversion overhaul plan.
 *
 * Client-side helper that derives whether SpotMe specialists are "online
 * now" based on `America/Mexico_City` time:
 *   • Mon-Sat 09:00-19:59 MX → online   ("En línea ahora")
 *   • Mon-Sat 05:00-08:59, 20:00-22:59  → delayed ("Responde en pocas horas")
 *   • All Sunday daytime (05:00-22:59)  → delayed
 *   • Every day 23:00-04:59 MX (quiet hours) → hidden (badge unmounts)
 *
 * Quiet hours exist because even "Responde en pocas horas" at 3am implies
 * availability that doesn't match reality (specialist is asleep). Hiding
 * the badge keeps the specialist card honest without making us promise a
 * faster reply than we can deliver. The 23-05 window is deliberately wide
 * so DST-style edge cases never accidentally show the green dot at night.
 *
 * The pure helper `computeBusinessHours(now)` is exported for testing — no
 * React, no API, no state. The `useBusinessHours()` hook wraps it with a
 * 5-minute interval tick so the badge auto-refreshes during long sessions.
 *
 * This module is client-only — the hook reads `new Date()` and runs an
 * interval, so it must never render on the server. Components consuming
 * it should either be dynamic-imported with `ssr: false` or be rendered
 * inside a tree that only hydrates client-side.
 *
 * See `docs/plans/2026-05-11-001-feat-listing-conversion-overhaul-plan.md`
 * (Unit 6) for the full design and acceptance criteria.
 */

import { useEffect, useState } from 'react'

import { I18nKey } from 'src/lib/listingVariants/types'

export type BusinessHoursResult = {
  online: boolean
  /**
   * `false` during quiet hours (23:00-04:59 MX, every day). Consumers
   * should render nothing rather than substituting a fallback string —
   * the intent is to keep the specialist card silent overnight.
   */
  visible: boolean
  copyKey: I18nKey
}

const TICK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const QUIET_START_HOUR = 23
const QUIET_END_HOUR = 5

// Pure helper — exported for testing. Mexico City stopped observing DST in
// 2022, so the timezone is UTC-6 year-round. `Intl.DateTimeFormat` is used
// instead of manual offset arithmetic so the conversion stays correct even
// if the policy changes again.
export function computeBusinessHours(now: Date): BusinessHoursResult {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Mexico_City',
    hour: 'numeric',
    hour12: false,
    weekday: 'short'
  }).formatToParts(now)

  const hourPart = parts.find((p) => p.type === 'hour')?.value ?? '0'
  const weekdayPart = parts.find((p) => p.type === 'weekday')?.value ?? ''

  // `hour12: false` can emit "24" at midnight in some ICU versions. Normalize
  // to 0-23 so the comparison below is well-defined.
  const rawHour = parseInt(hourPart, 10)
  const hour = rawHour === 24 ? 0 : rawHour
  const isSunday = weekdayPart === 'Sun'

  // Quiet hours wrap midnight (23-04 inclusive). `>= QUIET_START_HOUR` covers
  // 23:00-23:59; `< QUIET_END_HOUR` covers 00:00-04:59.
  const inQuietHours = hour >= QUIET_START_HOUR || hour < QUIET_END_HOUR
  const online = !inQuietHours && !isSunday && hour >= 9 && hour < 20

  return {
    online,
    visible: !inQuietHours,
    copyKey: online ? 'RESPONSE_BADGE.ONLINE' : 'RESPONSE_BADGE.DELAYED'
  }
}

export function useBusinessHours(): BusinessHoursResult {
  const [result, setResult] = useState<BusinessHoursResult>(() =>
    computeBusinessHours(new Date())
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setResult(computeBusinessHours(new Date()))
    }, TICK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  return result
}
